import axios from 'axios';

/**
 * Zoom threshold constants for layer visibility
 * These values match the configuration in config/cresiteiq2.php
 * Note: County layer (6-8) is currently disabled - will be added later
 */
const ZOOM_THRESHOLDS = {
    STATE_MIN: 4,
    STATE_MAX: 5,
    COUNTY_MIN: 6,
    COUNTY_MAX: 8,
    ZIP_MIN: 9,
    ZIP_MAX: 10,
    TRACT_MIN: 11,
    TRACT_MAX: 12,
    BG_MIN: 13,
    BG_MAX: 14,
    BLOCK_MIN: 15
};

/**
 * CRESiteIQ2 API Service
 * Handles all API communication for the GIS module
 */
class Cresiteiq2Api {
    constructor() {
        this.baseUrl = '/api/cresiteiq2';
        this.defaultTimeout = 180000; // 180 seconds (3 minutes) for large geographic queries with remote database
    }

    /**
     * Get layers data based on zoom, bounding box, and filters
     * @param {Object} params - Query parameters
     * @param {number} params.zoom - Map zoom level
     * @param {string} params.bbox - Bounding box string (west,south,east,north)
     * @param {string} params.layer - Layer type ('auto', 'states', 'zips', 'tracts', 'block_groups', 'blocks')
     * @param {boolean} params.includeCrime - Whether to include crime data
     * @param {string} params.crimeTypes - Comma-separated list of crime types to filter
     * @param {string} params.comparisonMode - Risk comparison mode ('viewport', 'state', 'zip')
     * @param {string} params.crimeType - Crime type for risk calculation (default: 'total_crime')
     * @param {boolean} params.includeAgs - Whether to include AGS data
     * @param {string} params.agsCategories - Comma-separated list of AGS categories
     * @param {string} params.agsVariable - Comma-separated list of AGS variable paths
     * @param {string} params.agsTemporalPeriod - AGS temporal period (year)
     * @param {string} params.agsComparisonMode - Comparison mode for AGS data ('viewport', 'state', 'county')
     * @param {AbortSignal} signal - Optional abort signal for request cancellation
     * @returns {Promise<Object>} GeoJSON FeatureCollection
     */
    async getLayers(params = {}, signal = null) {
        try {
            const config = {
                params: this.sanitizeParams(params),
                timeout: this.defaultTimeout,
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json'
                }
            };

            // Add abort signal if provided
            if (signal) {
                config.signal = signal;
            }

            const response = await axios.get(`${this.baseUrl}/layers`, config);

            return this.validateGeoJsonResponse(response.data);
        } catch (error) {
            // Re-throw abort errors as-is
            if (error.name === 'AbortError' || error.code === 'ERR_CANCELED') {
                throw error;
            }
            throw this.handleApiError(error);
        }
    }

    /**
     * Build bounding box string from Google Maps bounds
     * @param {google.maps.LatLngBounds} bounds - Google Maps bounds object
     * @returns {string} Bounding box string in format "west,south,east,north"
     */
    buildBboxString(bounds) {
        if (!bounds || typeof bounds.getNorthEast !== 'function') {
            throw new Error('Invalid bounds object provided');
        }

        const ne = bounds.getNorthEast();
        const sw = bounds.getSouthWest();

        let west = sw.lng();
        let east = ne.lng();
        const south = sw.lat();
        const north = ne.lat();

        // Handle International Date Line crossing
        // When crossing the date line, west > east
        if (west > east) {
            // Instead of using full range, split into two queries or use a more reasonable approach
            // For now, let's use a smaller reasonable bounding box that doesn't cross the date line
            // We'll prioritize the eastern part of the view
            if (east < 0) {
                // Eastern part is in western hemisphere, use that
                west = Math.max(west - 360, -180);
            } else {
                // Eastern part is in eastern hemisphere, use that  
                east = Math.min(east + 360, 180);
            }
        }

        // Ensure we don't exceed longitude bounds
        west = Math.max(-180, Math.min(180, west));
        east = Math.max(-180, Math.min(180, east));

        // Final check to ensure west < east
        if (west >= east) {
            // Fallback: use a reasonable default bounding box
            west = -180;
            east = 180;
        }

        return `${west},${south},${east},${north}`;
    }

    /**
     * Determine active layer based on zoom level
     * @param {number} zoom - Current map zoom level
     * @returns {string} Layer name for display
     */
    determineActiveLayer(zoom) {
        if (zoom < ZOOM_THRESHOLDS.STATE_MIN) return 'No Layer';
        if (zoom <= ZOOM_THRESHOLDS.STATE_MAX) return 'States';
        if (zoom <= ZOOM_THRESHOLDS.COUNTY_MAX) return 'No Layer'; // Counties disabled until data available
        if (zoom <= ZOOM_THRESHOLDS.ZIP_MAX) return 'ZIP Codes';
        if (zoom <= ZOOM_THRESHOLDS.TRACT_MAX) return 'Census Tracts';
        if (zoom <= ZOOM_THRESHOLDS.BG_MAX) return 'Block Groups';
        return 'Blocks';
    }

    /**
     * Get appropriate layer parameter for API based on zoom
     * @param {number} zoom - Current map zoom level
     * @returns {string|null} Layer parameter for API, or null if no layer should be displayed
     */
    getLayerForZoom(zoom) {
        if (zoom < ZOOM_THRESHOLDS.STATE_MIN) return null;
        if (zoom <= ZOOM_THRESHOLDS.STATE_MAX) return 'states';
        if (zoom <= ZOOM_THRESHOLDS.COUNTY_MAX) return null; // Counties disabled until data available
        if (zoom <= ZOOM_THRESHOLDS.ZIP_MAX) return 'zips';
        if (zoom <= ZOOM_THRESHOLDS.TRACT_MAX) return 'tracts';
        if (zoom <= ZOOM_THRESHOLDS.BG_MAX) return 'block_groups';
        return 'blocks';
    }

    /**
     * Check if crime filters should be shown at current zoom level
     * @param {number} zoom - Current map zoom level
     * @returns {boolean} Whether crime filters should be displayed
     */
    shouldShowCrimeFilters(zoom) {
        return zoom >= 10; // Show for tracts, block groups and blocks (zoom 10+) where crime data is available
    }

    /**
     * Sanitize and validate API parameters
     * @param {Object} params - Raw parameters
     * @returns {Object} Sanitized parameters
     */
    sanitizeParams(params) {
        const sanitized = {};

        // Zoom validation
        if (params.zoom !== undefined) {
            const zoom = parseInt(params.zoom);
            if (isNaN(zoom) || zoom < 1 || zoom > 20) {
                throw new Error('Invalid zoom level. Must be between 1 and 20.');
            }
            sanitized.zoom = zoom;
        }

        // Bounding box validation
        if (params.bbox) {
            if (!this.isValidBbox(params.bbox)) {
                throw new Error('Invalid bounding box format. Expected: west,south,east,north');
            }
            sanitized.bbox = params.bbox;
        }

        // Layer validation
        if (params.layer) {
            const validLayers = ['auto', 'states', 'zips', 'tracts', 'block_groups', 'blocks'];
            if (!validLayers.includes(params.layer)) {
                throw new Error(`Invalid layer. Must be one of: ${validLayers.join(', ')}`);
            }
            sanitized.layer = params.layer;
        }

        // Crime data inclusion
        if (params.includeCrime !== undefined) {
            sanitized.includeCrime = params.includeCrime ? 1 : 0;
        }

        // Crime types filtering
        if (params.crimeTypes) {
            const validCrimeTypes = [
                'total_crime', 'personal_crime', 'property_crime', 'murder', 'rape',
                'robbery', 'assault', 'burglary', 'larceny', 'motor_vehicle_theft'
            ];

            const requestedTypes = params.crimeTypes.split(',').map(t => t.trim());
            const invalidTypes = requestedTypes.filter(t => !validCrimeTypes.includes(t));

            if (invalidTypes.length > 0) {
                throw new Error(`Invalid crime types: ${invalidTypes.join(', ')}`);
            }

            sanitized.crimeTypes = requestedTypes.join(',');
        }

        // Comparison mode validation
        if (params.comparisonMode) {
            const validModes = ['viewport', 'state', 'county'];
            if (!validModes.includes(params.comparisonMode)) {
                throw new Error(`Invalid comparison mode. Must be one of: ${validModes.join(', ')}`);
            }
            sanitized.comparisonMode = params.comparisonMode;
        }

        // Crime type for risk calculation
        if (params.crimeType) {
            const validCrimeTypes = [
                'total_crime', 'personal_crime', 'property_crime', 'murder', 'rape',
                'robbery', 'assault', 'burglary', 'larceny', 'motor_vehicle_theft'
            ];

            if (!validCrimeTypes.includes(params.crimeType)) {
                throw new Error(`Invalid crime type. Must be one of: ${validCrimeTypes.join(', ')}`);
            }
            sanitized.crimeType = params.crimeType;
        }

        // AGS data inclusion
        if (params.includeAgs !== undefined) {
            sanitized.includeAgs = params.includeAgs ? 1 : 0;
        }

        // AGS categories
        if (params.agsCategories) {
            // Basic validation - categories should be comma-separated strings
            const categories = params.agsCategories.split(',').map(c => c.trim()).filter(c => c.length > 0);
            if (categories.length > 0) {
                sanitized.agsCategories = categories.join(',');
            }
        }

        // AGS variables
        if (params.agsVariable) {
            // Basic validation - variables should be comma-separated strings
            const variables = params.agsVariable.split(',').map(v => v.trim()).filter(v => v.length > 0);
            if (variables.length > 0) {
                sanitized.agsVariable = variables.join(',');
            }
        }

        // AGS temporal period
        if (params.agsTemporalPeriod) {
            // Basic validation - should be a year-like string
            const year = parseInt(params.agsTemporalPeriod);
            if (isNaN(year) || year < 2000 || year > 2050) {
                throw new Error('Invalid AGS temporal period. Must be a year between 2000 and 2050.');
            }
            sanitized.agsTemporalPeriod = params.agsTemporalPeriod;
        }

        if (params.agsComparisonMode) {
            const validModes = ['viewport', 'state', 'county'];
            if (!validModes.includes(params.agsComparisonMode)) {
                throw new Error(`Invalid AGS comparison mode. Must be one of: ${validModes.join(', ')}`);
            }
            sanitized.agsComparisonMode = params.agsComparisonMode;
        }

        // Scale directions (for multi-variable mode)
        if (params.scaleDirections && typeof params.scaleDirections === 'object') {
            sanitized.scaleDirections = params.scaleDirections;
        }

        // Weights (for multi-variable mode)
        if (params.weights && typeof params.weights === 'object') {
            sanitized.weights = params.weights;
        }

        return sanitized;
    }

    /**
     * Validate bounding box format
     * @param {string} bbox - Bounding box string
     * @returns {boolean} Whether the bbox is valid
     */
    isValidBbox(bbox) {
        if (typeof bbox !== 'string') return false;

        const parts = bbox.split(',');
        if (parts.length !== 4) return false;

        const coords = parts.map(p => parseFloat(p.trim()));
        if (coords.some(c => isNaN(c))) return false;

        const [west, south, east, north] = coords;

        // Basic coordinate validation
        if (west < -180 || west > 180) return false;
        if (east < -180 || east > 180) return false;
        if (south < -90 || south > 90) return false;
        if (north < -90 || north > 90) return false;

        // Logical validation
        // Allow west == east for full longitude range (date line crossing)
        if (west > east) return false;
        if (south >= north) return false;

        return true;
    }

    /**
     * Validate GeoJSON response structure
     * @param {Object} data - Response data
     * @returns {Object} Validated GeoJSON data
     */
    validateGeoJsonResponse(data) {
        if (!data || typeof data !== 'object') {
            throw new Error('Invalid response: Expected object');
        }

        if (data.type !== 'FeatureCollection') {
            throw new Error('Invalid response: Expected GeoJSON FeatureCollection');
        }

        if (!Array.isArray(data.features)) {
            throw new Error('Invalid response: Features must be an array');
        }

        // Validate each feature
        data.features.forEach((feature, index) => {
            if (!feature.type || feature.type !== 'Feature') {
                throw new Error(`Invalid feature at index ${index}: Missing or invalid type`);
            }

            if (!feature.geometry || !feature.geometry.type) {
                throw new Error(`Invalid feature at index ${index}: Missing geometry`);
            }

            if (!feature.properties || typeof feature.properties !== 'object') {
                throw new Error(`Invalid feature at index ${index}: Missing or invalid properties`);
            }
        });

        return data;
    }

    /**
     * Handle API errors and provide user-friendly messages
     * @param {Error} error - Axios error object
     * @returns {Error} Formatted error with additional context
     */
    handleApiError(error) {
        // Create enhanced error object with additional context
        const enhancedError = new Error();
        enhancedError.originalError = error;
        enhancedError.timestamp = new Date().toISOString();

        if (error.response) {
            // Server responded with error status
            const status = error.response.status;
            const data = error.response.data;

            enhancedError.statusCode = status;
            enhancedError.responseData = data;

            switch (status) {
                case 400:
                    enhancedError.message = data.message || 'Invalid request parameters';
                    enhancedError.type = 'validation';
                    break;
                case 401:
                    enhancedError.message = 'Authentication required';
                    enhancedError.type = 'authentication';
                    break;
                case 403:
                    enhancedError.message = 'Access denied';
                    enhancedError.type = 'authorization';
                    break;
                case 404:
                    enhancedError.message = 'API endpoint not found';
                    enhancedError.type = 'not_found';
                    break;
                case 422: {
                    const errors = data.errors || {};
                    const errorMessages = Object.values(errors).flat();
                    enhancedError.message = errorMessages.join(', ') || 'Validation failed';
                    enhancedError.type = 'validation';
                    enhancedError.validationErrors = errors;
                    break;
                }
                case 429:
                    enhancedError.message = 'Too many requests. Please try again later.';
                    enhancedError.type = 'rate_limit';
                    break;
                case 500:
                    enhancedError.message = 'Server error. Please try again later.';
                    enhancedError.type = 'server_error';
                    break;
                case 504:
                    enhancedError.message = 'Gateway timeout. The server took too long to respond.';
                    enhancedError.type = 'timeout';
                    break;
                default:
                    enhancedError.message = data.message || `Server error (${status})`;
                    enhancedError.type = 'server_error';
            }
        } else if (error.request) {
            // Network error - request was made but no response received
            enhancedError.type = 'network';

            if (error.code === 'ECONNABORTED') {
                enhancedError.message = 'Request timeout. The server took too long to respond.';
                enhancedError.subType = 'timeout';
            } else if (error.code === 'ERR_NETWORK') {
                enhancedError.message = 'Network error. Please check your connection.';
                enhancedError.subType = 'connection';
            } else {
                enhancedError.message = 'Network error. Please check your connection.';
                enhancedError.subType = 'unknown';
            }
        } else {
            // Other error (e.g., request setup error)
            enhancedError.message = error.message || 'An unexpected error occurred';
            enhancedError.type = 'unknown';
        }

        // Log error details for debugging
        console.error('[CRESiteIQ2 API Error]', {
            message: enhancedError.message,
            type: enhancedError.type,
            statusCode: enhancedError.statusCode,
            timestamp: enhancedError.timestamp,
            originalError: error
        });

        return enhancedError;
    }

    /**
     * Get available AGS categories
     * @param {Object} params - Query parameters
     * @param {string} params.geographic_level - Geographic level filter
     * @returns {Promise<Object>} AGS categories response
     */
    async getAgsCategories(params = {}) {
        try {
            const response = await axios.get(`${this.baseUrl}/ags/categories`, {
                params: this.sanitizeAgsParams(params),
                timeout: 30000, // Shorter timeout for metadata requests
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json'
                }
            });

            return response.data;
        } catch (error) {
            throw this.handleApiError(error);
        }
    }

    /**
     * Sanitize AGS-specific parameters
     * @param {Object} params - Raw parameters
     * @returns {Object} Sanitized parameters
     */
    sanitizeAgsParams(params) {
        const sanitized = {};

        // Geographic level validation
        if (params.geographic_level) {
            const validLevels = ['state', 'zip', 'tract', 'block_group', 'block'];
            if (!validLevels.includes(params.geographic_level)) {
                throw new Error(`Invalid geographic level. Must be one of: ${validLevels.join(', ')}`);
            }
            sanitized.geographic_level = params.geographic_level;
        }

        return sanitized;
    }

    /**
     * Generate cache key for requests (enhanced for AGS data)
     * @param {Object} params - Request parameters
     * @returns {string} Cache key
     */
    generateCacheKey(params) {
        const keyParts = [
            params.layer || 'auto',
            params.zoom || 'unknown',
            params.bbox || 'nobbox',
            params.includeCrime ? 'crime' : 'nocrime',
            params.crimeTypes || 'alltypes',
            params.comparisonMode || 'viewport',
            params.crimeType || 'total_crime',
            params.includeAgs ? 'ags' : 'noags',
            params.agsCategories || 'noagscats',
            params.agsTemporalPeriod || '2025'
        ];

        return `cresiteiq2:${keyParts.join(':')}`;
    }
}

// Export singleton instance and constants
export default new Cresiteiq2Api();
export { ZOOM_THRESHOLDS };
